import {Component} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {IBaseCRUModalComponent} from '../../../../sdk/src/components/ibase-cru-modal/ibase-cru-modal.component';
import {CpmCUserApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-global/cpmc-user.api-model';
import {
    CpmCUserCreateApiSchema,
    CpmCUserUpdateApiSchema
} from '../../../../gen/cpm-c-api-ts/src/schemas/c-global/cpmc-user.api-schemas';
import {EDialogOpenMode} from '../../../../sdk/src/enums/dialog-open-mode.enum';
import {CpmCUserApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-global/cpmc-user.api-service';
import {PrimeNgImportsModule} from '../../../../sdk/src/prime-ng-imports';
import {firstValueFrom} from 'rxjs';
import {CpmCUserRoleApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-common/cpmc-user-role.api-service';
import {CpmCUserRoleApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-common/cpmc-user-role.api-model';
import {CpmCUserApiUtils} from '../../../../gen/cpm-c-api-ts/src/utils/c-global/cpmc-user.api-utils';
import {
    ECpmCUserRoleTypeApiEnum,
    ECpmCUserTypeApiEnum,
    RECpmCUserTypeApiEnum
} from '../../../../gen/cpm-c-api-ts/src/enums/cpmc-sdk.api-enums';
import {SdkImportsModule} from '../../../../sdk/src/sdk-imports';
import {UserUtils} from '../../utils/user.utils';
import {DateUtils} from '../../../../sdk/src/utils/date.utils';

export class NewEditUserModalConfig {
    constructor(public existing?: CpmCUserApiModel) {}
}

export class NewEditUserModalResult {
    constructor(
        public item?: CpmCUserApiModel,
        public secretKey?: string
    ) {}
}

/**
 * New/Edit User modal
 */
@Component({
    selector: 'app-new-edit-user-modal',
    templateUrl: './new-edit-user-modal.component.html',
    imports: [ReactiveFormsModule, PrimeNgImportsModule, SdkImportsModule]
})
export class NewEditUserModalComponent extends IBaseCRUModalComponent<
    CpmCUserApiModel,
    NewEditUserModalComponent,
    NewEditUserModalConfig,
    NewEditUserModalResult
> {
    protected readonly UserOO8CPMApiModelUtils = CpmCUserApiUtils;

    // ===============================================================
    // ===============================================================

    protected item?: CpmCUserApiModel;
    protected userTypeOptions = UserUtils.typeOptions;
    protected globalUserRoles: CpmCUserRoleApiModel[] = [];

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     */
    constructor() {
        super();

        this.configure('UserApiModel', (model: CpmCUserApiModel) => model.email ?? '<Unknown user>');
        this.config.width = '30rem';
        this.contentMinHeight = '28rem';
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling is done in the parent)
     */
    protected async onReady(): Promise<void> {
        this.form = new FormGroup({
            type: new FormControl<string | null>(ECpmCUserTypeApiEnum.USER, [Validators.required]),
            firstname: new FormControl<string | null>(null, [Validators.required]),
            lastname: new FormControl<string | null>(null, []),
            email: new FormControl<string | null>(null, []),
            expiration_date: new FormControl<string | null>(null),
            password: new FormControl<string | null>(null, []),
            global_user_role_uuid: new FormControl<string | null>(null, [])
        });

        this.loadingStr = 'Loading global user roles...';
        this.globalUserRoles = (
            await firstValueFrom(
                CpmCUserRoleApiService.getUserRoles(
                    {
                        type: ECpmCUserRoleTypeApiEnum.GLOBAL
                    },
                    CpmCUserRoleApiService.dPaginatorConfig
                )
            )
        ).data;

        if (this.dialogOpenMode !== EDialogOpenMode.NEW) {
            this.loadingStr = 'Loading the user...';
            this.item = await firstValueFrom(
                CpmCUserApiService.getUserByUUID(this.config.data?.extended?.existing?.uuid ?? '')
            );

            if (CpmCUserApiUtils.is_user_as_machine_to_machine_user(this.item)) {
                this.userTypeOptions = UserUtils.typeOptions.filter((x) =>
                    CpmCUserApiUtils.is_user_type_a_machine_to_machine(RECpmCUserTypeApiEnum[x.value])
                );
            } else {
                this.userTypeOptions = UserUtils.typeOptions.filter(
                    (x) => !CpmCUserApiUtils.is_user_type_a_machine_to_machine(x.value)
                );
            }

            if (typeof this.item.expiration_date === 'string') {
                this.item.expiration_date = new Date(this.item.expiration_date);
            }

            this.form.patchValue(this.item);
        }

        // Update the form when the type change
        this.form.controls['type'].valueChanges.subscribe(this.onTypeChanged.bind(this));
        this.onTypeChanged();
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * On submit
     */
    protected async onSubmit() {
        if (!this.canSubmit()) {
            return;
        }

        if (
            this.isCreationMode &&
            CpmCUserApiUtils.is_user_type_a_machine_to_machine(this.form.controls['type'].value)
        ) {
            this.globalsService.confirmationService.confirm({
                header: 'Creation confirmation',
                message: 'Are you sure you want to create a machine to machine user?',
                accept: async () => await this.onSubmitConfirmed()
            });
        } else {
            await this.onSubmitConfirmed();
        }
    }
    /**
     * On submit (After potential validation)
     */
    private async onSubmitConfirmed() {
        if (!this.canSubmit()) {
            return;
        }

        const toSubmit: CpmCUserCreateApiSchema | CpmCUserUpdateApiSchema = this.isCreationMode
            ? {
                  type: this.form.controls['type'].value,
                  firstname: this.form.controls['firstname'].value,
                  lastname: this.form.controls['lastname']?.value,
                  email: this.form.controls['email']?.value,
                  password: this.form.controls['password']?.value,
                  expiration_date: DateUtils.toLocalISOString(this.form.controls['expiration_date']?.value),
                  global_user_role_uuid: this.form.controls['global_user_role_uuid']?.value
              }
            : {
                  type: this.form.controls['type'].value,
                  firstname: this.form.controls['firstname'].value,
                  lastname: this.form.controls['lastname']?.value,
                  email: this.form.controls['email']?.value,
                  expiration_date: DateUtils.toLocalISOString(this.form.controls['expiration_date']?.value),
                  global_user_role_uuid: this.form.controls['global_user_role_uuid']?.value
              };

        await this.submitCU(
            null,
            CpmCUserApiService.createUser,
            CpmCUserApiService.updateUser,
            this.item?.uuid,
            toSubmit,
            (resultCRUD: CpmCUserApiModel) => {
                let secretKey: string | undefined;
                if (CpmCUserApiUtils.is_user_as_machine_to_machine_user(resultCRUD)) {
                    secretKey = (resultCRUD as unknown as Record<string, string>)['client_secret'];
                }

                return new NewEditUserModalResult(resultCRUD, secretKey);
            }
        );
    }

    // ===============================================================
    // ===============================================================

    /**
     * When the User type changed
     */
    protected onTypeChanged() {
        this.form.controls['email'].clearValidators();
        this.form.controls['lastname'].clearValidators();
        this.form.controls['password'].clearValidators();

        if (!CpmCUserApiUtils.is_user_type_a_machine_to_machine(this.form.controls['type'].value)) {
            this.form.controls['lastname'].setValidators([Validators.required]);
            this.form.controls['email'].setValidators([Validators.required, Validators.email]);

            if (this.isCreationMode) this.form.controls['password'].setValidators([Validators.required]);
        }

        this.form.controls['email'].updateValueAndValidity();
        this.form.controls['lastname'].updateValueAndValidity();
        this.form.controls['password'].updateValueAndValidity();

        this.form.updateValueAndValidity();
    }
}
